import React from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { useQueryParams } from "../utils/queryParams";
import { getIsMobileApp } from '../../utils/mobileApp';

// components
import { Link as MUILink, Stack, useTheme, useMediaQuery, Button, Popper, Grow, Paper, ClickAwayListener, MenuList, MenuItem, Chip } from '@mui/material';
import AuthButton from './AuthButton';
import ToolbarDrawer from './ToolbarDrawer';
import SportNotificationBar from './SportNotificationBar';
// import SecondaryNav from './SecondaryNav'; todo if want to unify this for desktop && mweb


// configs
import { primaryLinks, simsLinks, analyticsLinks, allPrimaryLinks, secondaryLinks } from '../../configs/nav';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedApp } from './store/actions';
import { App } from './store/types';
import { DEFAULT_PROPS_LEAGUE } from '../stokasticProps/data/leagueConfig';
import { QuestionMark } from '@mui/icons-material';

export const viewColors = {
  slates: '#015ACE',
  contest: '#007636',
  pre: '#015ACE',
  late: '#DB4D00',
  post: '#000C78',
  projections: 'rgba(125, 125, 125, .25)',
  singlelineupsim: 'rgba(161, 0, 18, 0.15)',
  nbaRates: '#00897B',
}

const Header = () => {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const selectedApp: App = useSelector((state: any) => state.common.selectedApp);
  const selectedSport = useSelector((state: any) => state.common.selectedSport);
  const dispatch = useDispatch();
  const location = useLocation();
  const params: { league: string } = useParams();
  const { league: urlLeague } = params;
  // Use URL param if available, otherwise fall back to Redux selectedSport, then default to NBA
  const league = urlLeague || selectedSport?.sport || 'NBA';
  const queryParams = useQueryParams();
  const siteColor = viewColors[selectedApp];
  // Entering Props lands on the seasonal default league; once inside Props, keep the user's league
  const propsLeague = selectedApp === App.PROPS ? league : DEFAULT_PROPS_LEAGUE;
  const toolItems = primaryLinks(league, queryParams?.toString(), propsLeague) || [];
  const simsItems = simsLinks(league, queryParams?.toString()) || [];
  const analyticsItems = analyticsLinks(league, queryParams?.toString()) || [];
  const allToolItems = allPrimaryLinks(league, queryParams?.toString(), propsLeague) || [];

  // Check if any sim tool is currently active
  const isSimActive = simsItems.some(item => item.view === selectedApp);
  // Check if any analytics tool is currently active
  const isAnalyticsActive = analyticsItems.some(item => item.view === selectedApp);

  React.useEffect(() => {
    if (location) {
      if (location.pathname.includes('schedule')) {
        dispatch(setSelectedApp(App.SLATES));
      } else if (location.pathname.includes('props')) {
        dispatch(setSelectedApp(App.PROPS));
      } else if (location.pathname.includes('pre')) {
        dispatch(setSelectedApp(App.PRE_CONTEST_SIMULATOR));
      } else if (location.pathname.includes('post')) {
        dispatch(setSelectedApp(App.POST_CONTEST_SIMULATOR));
      } else if (location.pathname.includes('contest')) {
        dispatch(setSelectedApp(App.CONTEST_GENERATOR));
      } else if (location.pathname.includes('late')) {
        dispatch(setSelectedApp(App.LATE_SWAP_SIMULATOR));
      } else if (location.pathname.includes('singlelineupsim')) {
        dispatch(setSelectedApp(App.SINGLE_LINEUP_SIMULATOR));
      } else if (location.pathname.includes('nbarates')) {
        dispatch(setSelectedApp(App.NBA_RATES));
      } else if (location.pathname.includes('rotations')) {
        dispatch(setSelectedApp(App.ROTATIONS));
      }
      else {
        dispatch(setSelectedApp(App.DATA_HUB));
      }
    }
  }, [location.pathname, dispatch])

  // Hide header when rendered inside the mobile app WebView.
  // All hooks above must still be called (React Rules of Hooks) — the useEffect
  // syncing selectedApp from the URL is critical for the Props tool's Redux state.
  const isMobileApp = getIsMobileApp();
  if (isMobileApp) {
    return null;
  }

  if (isMobileView && !isMobileApp) {
    return (
      <>
        <ToolbarDrawer
          isMobileView={isMobileView}
          league={league}
          selectedView={selectedApp}
          secondaryLinks={secondaryLinks?.[league?.toUpperCase()] || secondaryLinks?.default}
          toolItems={allToolItems}
        />
        <SportNotificationBar />
      </>
    )
  }
  return (
    <>
    <Stack direction='row' alignItems='center' bgcolor="#000A16" justifyContent='space-between' sx={{
      height: 50,
      padding: { xs: '0 12px', lg: '0 20px' },
      borderBottom: `1px solid ${siteColor}`,
    }}>
      <Stack direction='row' alignItems='center' columnGap={{ xs: 1, lg: 3 }} height='100%' flex={1} minWidth={0}>
        {isMobileApp ? (
          <Stack sx={{
            width: 65,
            minWidth: 65,
            height: 20,
            '& img': {
              height: '100%',
              width: '100%',
              objectFit: 'contain',
            }
          }}>
            <img
              src={`${process.env.PUBLIC_URL}/images/logo.png`}
              alt="Stokastic Tools"
            />
          </Stack>
        ) : (
          <Link to="/" style={{ textDecoration: 'none' }}>
            <Stack sx={{
              width: 65,
              minWidth: 65,
              height: 20,
              cursor: 'pointer',
              '& img': {
                height: '100%',
                width: '100%',
                objectFit: 'contain',
              }
            }}>
              <img
                src={`${process.env.PUBLIC_URL}/images/logo.png`}
                alt="Stokastic Tools"
              />
            </Stack>
          </Link>
        )}
        {!isMobileApp && (
          <Stack direction='row' alignItems='center' columnGap={2} sx={{
            height: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            '& .link': {
              color: 'white !important',
              fontSize: { xs: '12px', lg: '16px' },
              fontWeight: 500,
              fontFamily: 'Inter',
              textDecoration: 'none',
              height: '100%',
              margin: 'auto 0',
              display: 'flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
              '&:hover': {
                background: `linear-gradient(90deg, rgba(0, 118, 54, 0.00) 0%, ${siteColor} 52%, rgba(0, 118, 54, 0.00) 100%)`,
                fontWeight: 900,
              },
            },
            '& .active': {
              background: `linear-gradient(90deg, rgba(0, 118, 54, 0.00) 0%, ${siteColor} 52%, rgba(0, 118, 54, 0.00) 100%)`,
              fontWeight: 900,
            }
          }}>
            <NavDropdown
              name="Sims"
              subItems={simsItems}
              selectedView={selectedApp}
              isActive={isSimActive}
              siteColor={siteColor}
            />
            {
              toolItems.map((link, index) => (
                <Link to={link.link} className={`link ${selectedApp === link.view && 'active'}`} onClick={() => dispatch(setSelectedApp(link.view))} key={link.name + index}>
                  {link.name}
                  {link.isBeta && (
                    <Chip
                      label="Beta"
                      size="small"
                      sx={{
                        ml: 0.5,
                        height: 18,
                        fontSize: '10px',
                        fontWeight: 600,
                        backgroundColor: '#42A5F5',
                        color: '#000',
                        '& .MuiChip-label': {
                          px: 0.75,
                        },
                      }}
                    />
                  )}
                </Link>
              ))
            }
            <NavDropdown
              name="Analytics"
              subItems={analyticsItems}
              selectedView={selectedApp}
              isActive={isAnalyticsActive}
              siteColor={siteColor}
            />
          </Stack>
        )}
      </Stack>
      {/* todo: swap out with <SecondaryNav /> */}
      <Stack direction='row' justifySelf='flex-end' display='flex' columnGap={2} flexShrink={0} sx={{ '& a': { display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'white', fontSize: '12px', } }} >
        {!isMobileApp && (
          <MUILink href="https://stokastic.com" target='_blank'>
            <Stack direction='row' alignItems='center' width={'100%'}>
              <Stack height={24} width={24}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/Stokastic_Logo_-25.png`}
                  alt=""
                />
              </Stack>
              Stokastic Home
            </Stack>
          </MUILink>
        )}
        {!isMobileApp && (
          <Stack direction="row" alignItems='center'>
            <Stack direction="row" alignItems='center'>
              <QuestionMark fontSize='small'/>
            </Stack>
            <DropdownMenu
              name="Help"
              selectedView={selectedApp}
              subItems={[
                {
                  name: 'Discord',
                  link: 'https://discord.gg/q9dqRMJ5Gu',
                  view: App.EXTERNAL,
                  linkOut: true,
                },
                {
                  name: 'Stokastic Youtube',
                  link: 'https://www.youtube.com/channel/UCBUUOH0LmDuWii7zBV7SKrQ',
                  view: App.EXTERNAL,
                  linkOut: true,
                },
              ]}
            />
          </Stack>
        )}
        <Stack display='flex' alignItems='center' justifyContent={'center'}>
          <AuthButton />
        </Stack>
      </Stack>
    </Stack>
    <SportNotificationBar />
    </>
  )
}

const NavDropdown = (props: {
  name: string,
  subItems: { name: string, link: string, view: string, description?: string, disabled?: boolean, linkOut?: boolean }[],
  selectedView: App,
  isActive: boolean,
  siteColor: string,
}) => {
  const { name, subItems, selectedView, isActive, siteColor } = props;
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const history = useHistory();
  const dispatch = useDispatch();

  const handleMouseEnter = () => {
    setOpen(true);
  };

  const handleMouseLeave = () => {
    setOpen(false);
  };

  const handleItemClick = (link: string, view: string) => {
    setOpen(false);
    dispatch(setSelectedApp(view as App));
    history.push(link);
  };

  return (
    <div
      ref={anchorRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ height: '100%', display: 'flex', alignItems: 'center' }}
    >
      <Button
        variant='text'
        sx={{
          textTransform: 'none',
          padding: '0 8px',
          height: '100%',
          color: 'white',
          minWidth: 'auto',
          fontSize: { xs: '12px', lg: '16px' },
          fontWeight: isActive || open ? 900 : 500,
          fontFamily: 'Inter',
          background: isActive || open
            ? `linear-gradient(90deg, rgba(0, 118, 54, 0.00) 0%, ${siteColor} 52%, rgba(0, 118, 54, 0.00) 100%)`
            : 'transparent',
          '&:hover': {
            background: `linear-gradient(90deg, rgba(0, 118, 54, 0.00) 0%, ${siteColor} 52%, rgba(0, 118, 54, 0.00) 100%)`,
            fontWeight: 900,
          },
        }}
      >
        {name}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        sx={{
          zIndex: 9999,
        }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper
              sx={{ bgcolor: '#1C1C1C', mt: 0, minWidth: 'auto' }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <MenuList
                id="sims-menu"
                sx={{ color: 'white', py: 1 }}
              >
                {subItems.map(item => (
                  <MenuItem
                    key={item.name}
                    onClick={() => {
                      if (item.disabled) return;
                      // an outside page (Ask The Sims on stokastic.com) opens in a new tab; the app router can't route it
                      if (item.linkOut) { setOpen(false); window.open(item.link, '_blank', 'noopener,noreferrer'); return; }
                      handleItemClick(item.link, item.view);
                    }}
                    disabled={item.disabled}
                    sx={{
                      fontSize: '14px',
                      fontFamily: 'Inter',
                      py: 1,
                      pl: 1,
                      pr: 2,
                      backgroundColor: selectedView === item.view ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      fontWeight: selectedView === item.view ? 700 : 400,
                      '&:hover': {
                        backgroundColor: item.disabled ? 'transparent' : 'rgba(255, 255, 255, 0.15)',
                      },
                      '&.Mui-disabled': {
                        opacity: 0.5,
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  >
                    {item.name}
                  </MenuItem>
                ))}
              </MenuList>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

const DropdownMenu = (props: { name: string, subItems: { name: string, link: string, view: string, linkOut?: boolean }[], selectedView: App }) => {
  const { name, subItems, selectedView } = props;
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const history = useHistory();

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <div onMouseLeave={handleToggle}>
      <Button
        ref={anchorRef}
        id="composition-button"
        aria-controls={open ? 'composition-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        onMouseEnter={handleToggle}
        variant='text'
        className={`link ${selectedView === subItems[0].view && 'active'}`}
        sx={{
          textTransform: 'none',
          padding: 0,
          height: '50px !important',
          color: 'white',
          minWidth: 'auto',
        }}
      >
        {name}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom"
        transition
        disablePortal
        sx={{
          zIndex: 9999,
        }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper sx={{
              bgcolor: '#1C1C1C'
            }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="composition-menu"
                  aria-labelledby="composition-button"
                  onKeyDown={handleListKeyDown}
                  sx={{
                    color: 'white',
                  }}
                >
                  {
                    subItems.map(sub => (
                      sub.linkOut ? (
                        <MenuItem 
                          key={sub.name}
                          component="a"
                          href={sub.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: 'white',
                            textDecoration: 'none',
                          }}
                          className={`${selectedView === sub.view && 'active'}`}
                        >
                          {sub.name}
                        </MenuItem>
                      ) : (
                        <MenuItem 
                          key={sub.name}
                          className={`link ${selectedView === sub.view && 'active'}`}
                          onClick={(e) => {
                            handleClose(e);
                            history.push(sub.link);
                          }}
                        >
                          {sub.name}
                        </MenuItem>
                      )
                    ))
                  }
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  )
}

export default Header;